import { BorderStyle, Box, Color } from '@gousto-internal/citrus-react'
import type { DashboardExperienceOrderDeliveryDetailsResponse } from '@library/api-dashboard-experience'
import React, { type CSSProperties, useLayoutEffect, useRef, useState } from 'react'
import { ManageDeliveryPanelContent } from '../ManageDeliveryPanelContent'

const MANAGE_DELIVERY_PANEL_VERTICAL_PADDING = 16
const MANAGE_DELIVERY_PANEL_EXPANDED_BORDER_TOP_WIDTH = 1
const DEFAULT_EXPANDED_PANEL_CONTENT_HEIGHT = 320
const EXPANDED_PANEL_VERTICAL_CHROME =
  MANAGE_DELIVERY_PANEL_VERTICAL_PADDING * 2 + MANAGE_DELIVERY_PANEL_EXPANDED_BORDER_TOP_WIDTH
const DEFAULT_EXPANDED_PANEL_HEIGHT =
  DEFAULT_EXPANDED_PANEL_CONTENT_HEIGHT + EXPANDED_PANEL_VERTICAL_CHROME

const getManageDeliveryPanelStyle = ({
  borderTopColor,
  expandedContentHeight,
  isManageDeliveryExpanded,
}: {
  borderTopColor?: string
  expandedContentHeight: number
  isManageDeliveryExpanded: boolean
}): CSSProperties => ({
  borderTopWidth: isManageDeliveryExpanded
    ? `${MANAGE_DELIVERY_PANEL_EXPANDED_BORDER_TOP_WIDTH}px`
    : '0px',
  borderTopColor,
  maxHeight: isManageDeliveryExpanded ? `${expandedContentHeight}px` : '0px',
  opacity: isManageDeliveryExpanded ? 1 : 0,
  overflow: 'hidden',
  paddingTop: isManageDeliveryExpanded ? `${MANAGE_DELIVERY_PANEL_VERTICAL_PADDING}px` : '0px',
  paddingBottom: isManageDeliveryExpanded ? `${MANAGE_DELIVERY_PANEL_VERTICAL_PADDING}px` : '0px',
  pointerEvents: isManageDeliveryExpanded ? 'auto' : 'none',
  transition:
    'max-height 240ms ease, opacity 240ms ease, padding-top 240ms ease, padding-bottom 240ms ease, border-top-width 240ms ease',
})

type ResponsiveManageDeliveryPanelProps = {
  currentOrderDeliveryDate: string
  currentOrderDeliveryDateLabel: string
  hasSaveDeliveryDateError: boolean
  hasSkipOrderError: boolean
  isManageDeliveryExpanded: boolean
  isSavingDeliveryDate: boolean
  isSkipBoxDisabled: boolean
  isSkipOrder: boolean
  onSaveDeliveryDate: (selection: { date: string; formattedDate: string }) => void | Promise<void>
  onSkipBoxClick: () => void
  onTrackBoxClick: (url: string) => void
  orderDeliveryDetails: DashboardExperienceOrderDeliveryDetailsResponse | undefined
  orderState: string
}

export const ResponsiveManageDeliveryPanel = ({
  currentOrderDeliveryDate,
  currentOrderDeliveryDateLabel,
  hasSaveDeliveryDateError,
  hasSkipOrderError,
  isManageDeliveryExpanded,
  isSavingDeliveryDate,
  isSkipBoxDisabled,
  isSkipOrder,
  onSaveDeliveryDate,
  onSkipBoxClick,
  onTrackBoxClick,
  orderDeliveryDetails,
  orderState,
}: ResponsiveManageDeliveryPanelProps) => {
  const mobileContentRef = useRef<HTMLDivElement>(null)
  const desktopContentRef = useRef<HTMLDivElement>(null)
  const [expandedContentHeight, setExpandedContentHeight] = useState(DEFAULT_EXPANDED_PANEL_HEIGHT)

  useLayoutEffect(() => {
    const nextExpandedContentHeight = Math.max(
      mobileContentRef.current?.scrollHeight ?? 0,
      desktopContentRef.current?.scrollHeight ?? 0,
    )

    if (nextExpandedContentHeight > 0) {
      // Include the panel's vertical padding and border in the expanded height.
      setExpandedContentHeight(nextExpandedContentHeight + EXPANDED_PANEL_VERTICAL_CHROME)
    }
  }, [
    currentOrderDeliveryDate,
    currentOrderDeliveryDateLabel,
    hasSaveDeliveryDateError,
    hasSkipOrderError,
    isSavingDeliveryDate,
    isSkipBoxDisabled,
    isSkipOrder,
    orderDeliveryDetails,
    orderState,
  ])

  return (
    <>
      <Box
        display={['block', 'block', 'none']}
        id="manage-delivery-panel"
        aria-hidden={!isManageDeliveryExpanded}
        data-testid="manage-delivery-panel"
        bg={Color.NeutralGrey_50}
        borderColor={Color.ColdGrey_100}
        borderStyle={BorderStyle.Solid}
        paddingH={4}
        style={getManageDeliveryPanelStyle({
          borderTopColor: isManageDeliveryExpanded ? '#FFFFFF' : undefined,
          expandedContentHeight,
          isManageDeliveryExpanded,
        })}
      >
        <div ref={mobileContentRef}>
          <ManageDeliveryPanelContent
            currentOrderDeliveryDate={currentOrderDeliveryDate}
            currentOrderDeliveryDateLabel={currentOrderDeliveryDateLabel}
            hasSaveDeliveryDateError={hasSaveDeliveryDateError}
            hasSkipOrderError={hasSkipOrderError}
            isSavingDeliveryDate={isSavingDeliveryDate}
            isSkipBoxDisabled={isSkipBoxDisabled}
            isSkipOrder={isSkipOrder}
            onSaveDeliveryDate={onSaveDeliveryDate}
            onSkipBoxClick={onSkipBoxClick}
            onTrackBoxClick={onTrackBoxClick}
            orderDeliveryDetails={orderDeliveryDetails}
            orderState={orderState}
          />
        </div>
      </Box>
      <Box
        display={['none', 'none', 'block']}
        aria-hidden={!isManageDeliveryExpanded}
        bg={Color.NeutralGrey_50}
        borderColor={Color.ColdGrey_100}
        borderStyle={BorderStyle.Solid}
        paddingH={4}
        style={getManageDeliveryPanelStyle({
          expandedContentHeight,
          isManageDeliveryExpanded,
        })}
      >
        <div ref={desktopContentRef}>
          <ManageDeliveryPanelContent
            currentOrderDeliveryDate={currentOrderDeliveryDate}
            currentOrderDeliveryDateLabel={currentOrderDeliveryDateLabel}
            hasSaveDeliveryDateError={hasSaveDeliveryDateError}
            hasSkipOrderError={hasSkipOrderError}
            isSavingDeliveryDate={isSavingDeliveryDate}
            isSkipBoxDisabled={isSkipBoxDisabled}
            isSkipOrder={isSkipOrder}
            onSaveDeliveryDate={onSaveDeliveryDate}
            onSkipBoxClick={onSkipBoxClick}
            onTrackBoxClick={onTrackBoxClick}
            orderDeliveryDetails={orderDeliveryDetails}
            orderState={orderState}
          />
        </div>
      </Box>
    </>
  )
}
